import type { JobStatus } from "@zenforge/shared";
import { useEffect, useState } from "react";
import { useApi } from "../api.js";
import { Notice, StatusBadge } from "../components.js";
import { useStickyState } from "../useStickyState.js";

export interface Named {
  id: string;
  name: string;
}

/** Which rung of the resolution ladder this node's generation would use:
 *  its subchapter's own PDF, the chapter's as fallback, or nothing at all.
 *  Null when the tree was fetched without a collection. */
export type Knowledge = "subChapter" | "chapter" | "none" | null;

export interface TopicNode {
  id: string;
  name: string;
  /** The main app's printed numbering ("3.6"), sent at every level. Null when
   *  the main app has none. */
  sequenceNo: number | null;
  jobs: Partial<Record<JobStatus, number>>;
  knowledge: Knowledge;
  /** The model the grounding PDF asks for (migration 047). Null when it names
   *  none, or when the tree was fetched without a collection. */
  suggestedModel?: string | null;
}

export interface SubChapterNode {
  id: string;
  name: string;
  sequenceNo: number | null;
  jobs: Partial<Record<JobStatus, number>>;
  knowledge: Knowledge;
  suggestedModel?: string | null;
  topics: TopicNode[];
}

export interface ChapterNode {
  id: string;
  name: string;
  sequenceNo: number | null;
  subChapters: SubChapterNode[];
}

export interface ChapterTree {
  id: string;
  board: string;
  class: string;
  subject: string;
  chapters: ChapterNode[];
}

export interface CollectionRow {
  id: number;
  name: string;
  model: string;
  vectorStoreId: string | null;
  /** The one syllabus this collection covers (migration 020). Null only on
   *  collections created before that existed. */
  syllabusId: string | null;
  board: string | null;
  className: string | null;
  subject: string | null;
}

export interface CategoryRow {
  id: number;
  name: string;
  code: string;
  categoryType?: "MODULE" | "BOOK_IN" | "BOOK_BACK";
  questionAssessmentType?: string | null;
  questionCategoryId?: string | null;
  responseType: "html" | "json" | "markdown";
  isActive: boolean;
}

export interface ScopeRow {
  chapterId: string;
  subChapterId: string | null;
  topicId: string | null;
}

export interface KnowledgeSourceRow {
  id: number;
  filename: string;
  roleTag?: string;
  indexingStatus: "pending" | "uploading" | "indexing" | "ready" | "failed";
  /** False for anything indexed before knowledge-source filtering existed —
   *  it can't be scoped to until it's re-uploaded. */
  filterable: boolean;
  scopes?: ScopeRow[];
}

/** "3.6 " when the main app numbers the node, "" when it does not — the
 *  same numbering the generation prompt uses to locate a target. */
export function seq(node: { sequenceNo: number | null }): string {
  return node.sequenceNo !== null ? `${node.sequenceNo}. ` : "";
}

export function toggle<T>(set: Set<T>, id: T, update: (next: Set<T>) => void) {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  update(next);
}

function jobBadges(jobs: Partial<Record<JobStatus, number>>) {
  const entries = Object.entries(jobs) as [JobStatus, number][];
  if (entries.length === 0) return null;
  return (
    <div className="row" style={{ gap: 6, marginLeft: "auto" }}>
      {entries.map(([status, count]) => (
        <span key={status} className="row" style={{ gap: 4 }}>
          <StatusBadge status={status} />
          <span className="mini">{count}</span>
        </span>
      ))}
    </div>
  );
}

/** Marks a node whose generation has no PDF behind it. Only "none" is worth
 *  showing — resolving to a topic's, subchapter's or chapter's PDF is the
 *  normal case and needs no decoration.
 *
 *  Shown on topics as well as subchapters since migration 022: a topic can
 *  carry its own PDF now, so its status is genuinely its own rather than a
 *  copy of the subchapter's. A topic reading "No PDF" under a subchapter
 *  that does not is impossible — the ladder falls back — so the two can
 *  only disagree in the direction that matters. */
/**
 * The model the PDF grounding this target asks for.
 *
 * Shown next to the PDF status because it is a property of that PDF, and
 * because it is what a job created for this target will be stamped with at
 * enqueue — so the number people see while selecting is the one their run will
 * actually use.
 *
 * Absent when the resolved PDF names no model: the collection default then
 * applies, which is the ordinary case and needs no decoration.
 */
function modelBadge(suggestedModel: string | null | undefined) {
  if (!suggestedModel) return null;
  return (
    <span
      className="target-model"
      title={`Grounding PDF asks for ${suggestedModel} — jobs for this target will run on it unless a per-run model is chosen`}
    >
      {suggestedModel}
    </span>
  );
}

function resolveGroundingSource(
  readySources: KnowledgeSourceRow[],
  chapterId: string | null,
  subChapterId: string,
  topicId?: string,
): KnowledgeSourceRow | null {
  // A topic only resolves a PDF if one was specifically uploaded directly for it.
  if (topicId) {
    return (
      readySources.find((s) =>
        s.scopes?.some((sc) => sc.topicId === topicId),
      ) ?? null
    );
  }
  const subSource = readySources.find((s) =>
    s.scopes?.some((sc) => sc.subChapterId === subChapterId && !sc.topicId),
  );
  if (subSource) return subSource;
  if (chapterId) {
    const chapterSource = readySources.find((s) =>
      s.scopes?.some((sc) => sc.chapterId === chapterId && !sc.subChapterId && !sc.topicId),
    );
    if (chapterSource) return chapterSource;
  }
  return readySources.find((s) => !s.scopes || s.scopes.length === 0) ?? null;
}

function knowledgeBadge(
  isTopic: boolean,
  source: KnowledgeSourceRow | null,
  fallbackKnowledge: Knowledge,
  onViewPdf?: (source: KnowledgeSourceRow) => void,
) {
  if (isTopic) {
    if (!source) {
      return (
        <span
          className="badge failed mini"
          title="No PDF mapped directly to this topic"
        >
          No PDF
        </span>
      );
    }
    return (
      <span
        className="badge info mini"
        role="button"
        style={{
          cursor: onViewPdf ? "pointer" : "default",
          userSelect: "none",
        }}
        title={`Grounding file: ${source.filename} — click to view PDF`}
        onClick={(e) => {
          if (onViewPdf) {
            e.preventDefault();
            e.stopPropagation();
            onViewPdf(source);
          }
        }}
      >
        📄 View PDF
      </span>
    );
  }

  // Subchapter rung
  if (!source || fallbackKnowledge === "none") {
    return (
      <span
        className="badge failed mini"
        title="No PDF mapped to this subchapter or its chapter"
      >
        No PDF
      </span>
    );
  }

  const label =
    source.scopes?.some((sc) => sc.subChapterId)
      ? "View PDF"
      : "Chapter PDF";

  return (
    <span
      className="badge info mini"
      role="button"
      style={{
        cursor: onViewPdf ? "pointer" : "default",
        userSelect: "none",
      }}
      title={`Grounding file: ${source.filename} — click to view PDF`}
      onClick={(e) => {
        if (onViewPdf) {
          e.preventDefault();
          e.stopPropagation();
          onViewPdf(source);
        }
      }}
    >
      📄 {label}
    </span>
  );
}

/**
 * Everything the two generation screens — Content Generation and Run Pipeline —
 * need in order to pick targets: the syllabus selection, the live chapter tree,
 * and the collection it resolves to.
 *
 * Shared as a hook rather than copied into each page because the two screens
 * differ only in what they *do* with the chosen targets. Duplicating the tree
 * would mean two copies of the sticky-state keys and the PDF-resolution rules,
 * which drift apart the first time only one of them is fixed.
 */
export function useGenerationTargets() {
  // Persisted: the syllabus a person works through for days at a time, and the
  // collection that drives categories and knowledge sources. Re-picking these
  // on every visit was the actual friction.
  //
  // Chapter persists too: it is context for the work, not a generation target
  // in itself, and resetSelection() still clears it whenever board/class/
  // subject changes, so a restored chapter can never outlive the syllabus it
  // belongs to.
  //
  // Deliberately NOT persisted: topics and subchapters below. Those ARE the
  // targets, and restoring a stale set means the Generate button acts on
  // things nobody chose this session — the one mistake here that costs real
  // money.
  //
  // The keys stay "zf.gen.*" across both screens on purpose: carrying the
  // syllabus over when someone moves between category and pipeline runs is
  // the whole point of persisting it.
  const [board, setBoard] = useStickyState<string | null>("zf.gen.board", null);
  const [className, setClassName] = useStickyState<string | null>("zf.gen.class", null);
  const [subject, setSubject] = useStickyState<string | null>("zf.gen.subject", null);
  const [chapterId, setChapterId] = useStickyState<string | null>("zf.gen.chapterId", null);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  // A subchapter is itself a valid target — "Introduction" subchapters often
  // have no topics under them at all, so this is the only way to generate
  // content for them.
  const [selectedSubChapters, setSelectedSubChapters] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  const collections = useApi<CollectionRow[]>("/collections");
  const boards = useApi<Named[]>("/boards");
  const classes = useApi<Named[]>("/classes");
  const subjects = useApi<Named[]>("/subjects");

  // The collection is no longer chosen: one collection covers one syllabus
  // (migration 020), so board/class/subject determines it.
  //
  // Matched on the cached board/class/subject rather than the tree's syllabus
  // id, deliberately: the tree request itself takes collectionId (for scope
  // annotation), so resolving from the tree would be circular.
  const collection = collections.data?.find(
    (c) => c.board === board && c.className === className && c.subject === subject,
  );
  const collectionId = collection?.id ?? null;

  // collectionId is part of the query: the tree comes back annotated with
  // which PDF each node resolves to, which is what surfaces unmapped targets
  // before anyone clicks Generate.
  const tree = useApi<ChapterTree>(
    board && className && subject
      ? `/chapter-tree?board=${encodeURIComponent(board)}&class=${encodeURIComponent(className)}&subject=${encodeURIComponent(subject)}${
          collectionId ? `&collectionId=${collectionId}` : ""
        }`
      : null,
    [board, className, subject, collectionId, refreshKey],
  );
  const knowledgeSources = useApi<KnowledgeSourceRow[]>(
    collectionId ? `/collections/${collectionId}/knowledge-sources` : null,
    [collectionId],
  );
  const readySources = knowledgeSources.data?.filter((s) => s.indexingStatus === "ready") ?? [];

  const chapter = tree.data?.chapters.find((c) => c.id === chapterId);
  const subChapters = chapter?.subChapters ?? [];
  const allTopics = subChapters.flatMap((sc) => sc.topics);

  /** Selected targets whose chapter and subchapter both have no PDF mapped.
   *  createJobs refuses the whole batch for these, so they are surfaced here
   *  first — a whole chapter selected with one unmapped subchapter would
   *  otherwise fail with nothing created. */
  const unmappedTargets = [
    ...subChapters.filter((sc) => selectedSubChapters.has(sc.id) && sc.knowledge === "none"),
    ...allTopics.filter((t) => selectedTopics.has(t.id) && t.knowledge === "none"),
  ];

  const targetCount = selectedTopics.size + selectedSubChapters.size;
  const failedTotal =
    allTopics.reduce((sum, topic) => sum + (topic.jobs.FAILED ?? 0), 0) +
    subChapters.reduce((sum, sc) => sum + (sc.jobs.FAILED ?? 0), 0);

  function resetSelection() {
    setChapterId(null);
    setSelectedTopics(new Set());
    setSelectedSubChapters(new Set());
  }

  // Jobs created here are picked up by the worker within a second, so the
  // statuses refresh on their own while a batch drains.
  useEffect(() => {
    if (!chapterId) return;
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 2500);
    return () => clearInterval(timer);
  }, [chapterId]);

  return {
    board,
    setBoard,
    className,
    setClassName,
    subject,
    setSubject,
    chapterId,
    setChapterId,
    selectedTopics,
    setSelectedTopics,
    selectedSubChapters,
    setSelectedSubChapters,
    boards,
    classes,
    subjects,
    collections,
    collection,
    collectionId,
    tree,
    subChapters,
    allTopics,
    readySources,
    unmappedTargets,
    targetCount,
    failedTotal,
    resetSelection,
    refresh: () => setRefreshKey((k) => k + 1),
    apiError: boards.error ?? classes.error ?? subjects.error ?? collections.error,
  };
}

export type GenerationTargets = ReturnType<typeof useGenerationTargets>;

/** The syllabus pickers: board → class → subject → chapter. */
export function SyllabusSelector({ t }: { t: GenerationTargets }) {
  return (
    <div className="card span-12 target-selector">
      <div className="row between">
        <div>
          <h2>Select content</h2>
          <div className="muted">Board → Class → Subject → Chapter → Subchapters &amp; Topics</div>
        </div>
        <button
          className="btn mini"
          disabled={!t.board && !t.className && !t.subject && !t.chapterId}
          onClick={() => {
            t.setBoard(null);
            t.setClassName(null);
            t.setSubject(null);
            // Clears chapter, topics and subchapters — the same reset the
            // syllabus dropdowns already do when one of them changes.
            t.resetSelection();
          }}
        >
          Clear selection
        </button>
      </div>

      <div className="row" style={{ gap: 16, alignItems: "flex-start" }}>
        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label>Board</label>
          <select
            value={t.board ?? ""}
            onChange={(e) => {
              t.setBoard(e.target.value || null);
              t.resetSelection();
            }}
          >
            <option value="">Select a board…</option>
            {t.boards.data?.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label>Class</label>
          <select
            value={t.className ?? ""}
            onChange={(e) => {
              t.setClassName(e.target.value || null);
              t.resetSelection();
            }}
          >
            <option value="">Select a class…</option>
            {t.classes.data?.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label>Subject</label>
          <select
            value={t.subject ?? ""}
            onChange={(e) => {
              t.setSubject(e.target.value || null);
              t.resetSelection();
            }}
          >
            <option value="">Select a subject…</option>
            {t.subjects.data?.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label>Chapter</label>
          <select
            value={t.chapterId ?? ""}
            disabled={!t.tree.data}
            onChange={(e) => {
              t.setChapterId(e.target.value || null);
              t.setSelectedTopics(new Set());
              t.setSelectedSubChapters(new Set());
            }}
          >
            <option value="">Select a chapter…</option>
            {t.tree.data?.chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {seq(c)}
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {t.board && t.className && t.subject && !t.tree.loading && !t.tree.data && (
        <p className="muted" style={{ marginTop: 8 }}>
          No syllabus for {t.board} / {t.className} / {t.subject} in the main app.
        </p>
      )}
    </div>
  );
}

/** The subchapter/topic checkbox tree. `onRetryFailed` is optional: only the
 *  category screen owns that action, since pipeline runs are retried per step
 *  from the executions screen instead. */
export function TargetTree({
  t,
  onRetryFailed,
  onViewPdf,
}: {
  t: GenerationTargets;
  onRetryFailed?: () => void;
  onViewPdf?: (source: KnowledgeSourceRow) => void;
}) {
  const [query, setQuery] = useState("");

  /**
   * Subchapters narrowed to the query, each keeping only its matching topics.
   *
   * A subchapter whose own name matches keeps all of its topics: matching
   * "Sets" should offer everything under Sets, not hide the topics because
   * their names say something else. A subchapter that matches only through a
   * topic keeps just that topic, so the list shows why it is still there.
   *
   * The sequence number is searched too — people refer to these as "3.6" far
   * more often than by name.
   */
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? t.subChapters
        .map((sc) => {
          const scText = `${seq(sc)}${sc.name}`.toLowerCase();
          if (scText.includes(needle)) return sc;
          const topics = sc.topics.filter((topic) =>
            `${seq(topic)}${topic.name}`.toLowerCase().includes(needle),
          );
          return topics.length > 0 ? { ...sc, topics } : null;
        })
        .filter((sc): sc is (typeof t.subChapters)[number] => sc !== null)
    : t.subChapters;

  // Select all applies to what is on screen. Acting on the whole chapter while
  // a filter hides most of it is the kind of surprise that gets noticed only
  // after the run is queued.
  const visibleTopics = visible.flatMap((sc) => sc.topics);

  // Selections the filter is hiding. Without this the count in the run panel
  // disagrees with the list for no visible reason.
  const visibleTopicIds = new Set(visibleTopics.map((topic) => topic.id));
  const visibleSubIds = new Set(visible.map((sc) => sc.id));
  const selectedHidden =
    [...t.selectedTopics].filter((id) => !visibleTopicIds.has(id)).length +
    [...t.selectedSubChapters].filter((id) => !visibleSubIds.has(id)).length;
  const allVisibleSelected =
    (visibleTopics.length > 0 || visible.length > 0) &&
    visibleTopics.every((topic) => t.selectedTopics.has(topic.id)) &&
    visible.every((sc) => t.selectedSubChapters.has(sc.id));

  return (
    <div className="card span-7 target-tree">
      <div className="row between target-tree-head">
        <h2>Subchapters &amp; Topics</h2>
        <div className="row">
          {onRetryFailed && t.failedTotal > 0 && (
            <button className="btn mini" onClick={onRetryFailed}>
              Retry Failed ({t.failedTotal})
            </button>
          )}
          {(t.allTopics.length > 0 || t.subChapters.length > 0) && (
            <button
              className="btn mini"
              disabled={visible.length === 0}
              onClick={() => {
                // Adds to or removes from the current selection rather than
                // replacing it: anything chosen under an earlier search must
                // survive the next one.
                const topics = new Set(t.selectedTopics);
                const subs = new Set(t.selectedSubChapters);
                for (const sc of visible) {
                  if (allVisibleSelected) subs.delete(sc.id);
                  else subs.add(sc.id);
                  for (const topic of sc.topics) {
                    if (allVisibleSelected) topics.delete(topic.id);
                    else topics.add(topic.id);
                  }
                }
                t.setSelectedTopics(topics);
                t.setSelectedSubChapters(subs);
              }}
            >
              {allVisibleSelected ? "Clear" : "Select"}
              {needle ? " matching" : " all"}
            </button>
          )}
        </div>
      </div>

      {t.chapterId && (t.subChapters.length > 0 || t.allTopics.length > 0) && (
        <div className="target-tree-search">
          <input
            type="search"
            value={query}
            placeholder="Filter by name or number…"
            onChange={(e) => setQuery(e.target.value)}
          />
          {needle && (
            <span className="mini muted">
              {visible.length} subchapter{visible.length === 1 ? "" : "s"},{" "}
              {visibleTopics.length} topic{visibleTopics.length === 1 ? "" : "s"}
              {selectedHidden > 0 && ` · ${selectedHidden} selected hidden`}
            </span>
          )}
        </div>
      )}

      <div className="target-tree-body">
      {!t.chapterId && <p className="muted">Select a chapter to see its subchapters and topics.</p>}

      {t.chapterId && needle && visible.length === 0 && (
        <p className="muted">Nothing matches "{query.trim()}".</p>
      )}

      {visible.map((sc) => {
        const scSource = resolveGroundingSource(t.readySources, t.chapterId, sc.id);
        return (
          <div key={sc.id} style={{ marginBottom: 14 }}>
            <label className="row" style={{ gap: 8, fontWeight: 600, padding: "3px 0" }}>
              <input
                type="checkbox"
                checked={t.selectedSubChapters.has(sc.id)}
                onChange={() => toggle(t.selectedSubChapters, sc.id, t.setSelectedSubChapters)}
              />
              <span>
                {seq(sc)}
                {sc.name}
              </span>
              {knowledgeBadge(false, scSource, sc.knowledge, onViewPdf)}
              {modelBadge(sc.suggestedModel)}
              <span className="row" style={{ gap: 6, marginLeft: "auto" }}>
                {jobBadges(sc.jobs)}
              </span>
            </label>

            <div style={{ paddingLeft: 28, marginTop: sc.topics.length > 0 ? 8 : 0 }}>
              {sc.topics.map((topic) => {
                const topicSource = resolveGroundingSource(t.readySources, t.chapterId, sc.id, topic.id);
                return (
                  <label
                    key={topic.id}
                    className="row"
                    style={{ gap: 8, fontWeight: 400, padding: "3px 0" }}
                  >
                    <input
                      type="checkbox"
                      checked={t.selectedTopics.has(topic.id)}
                      onChange={() => toggle(t.selectedTopics, topic.id, t.setSelectedTopics)}
                    />
                    <span>
                      {seq(topic)}
                      {topic.name}
                    </span>
                    {knowledgeBadge(true, topicSource, topic.knowledge, onViewPdf)}
                    {modelBadge(topic.suggestedModel)}
                    {jobBadges(topic.jobs) ?? (
                      <span className="badge pending mini" style={{ marginLeft: "auto" }}>
                        Not generated
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

/** The collection summary at the top of both screens' right-hand card. */
export function CollectionSummary({ t }: { t: GenerationTargets }) {
  return (
    <>
      <div className="field">
        <label>Collection</label>
        {t.collection ? (
          <div className="mini">
            <b>{t.collection.name}</b>
            <div className="muted">
              model: <code>{t.collection.model}</code> ·{" "}
              {t.collection.vectorStoreId ? "knowledge indexed" : "no knowledge source"}
            </div>
          </div>
        ) : (
          <span className="mini muted">
            {t.tree.data
              ? "No collection covers this syllabus yet."
              : "Choose a board, class and subject."}
          </span>
        )}
      </div>

      {t.tree.data && !t.collection && (
        <Notice kind="warn">
          Nothing can be generated for this syllabus until a collection is created for it.
        </Notice>
      )}
    </>
  );
}

/** The knowledge-grounding note shown under the picker on both screens. */
export function KnowledgeNote({ t }: { t: GenerationTargets }) {
  if (!t.collectionId) return null;
  return (
    <div className="field">
      <label>Knowledge</label>
      <span className="mini muted">
        Grounded in the PDF mapped to the target chapter or subchapter.
      </span>
      {t.readySources.length === 0 && (
        <Notice kind="warn">This collection has no indexed PDFs yet.</Notice>
      )}
    </div>
  );
}
